import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useCallback, useState } from 'react';
import { T } from '../../translation/src';
import { Button } from '../Button';
import { Modal } from './index';

export interface ConfirmDialogProps {
  open?: boolean;
  onClose: () => void;
  // Closes once it is done: a confirmation that takes a while keeps the dialog, busy, until it resolves.
  onConfirm: () => void | Promise<void>;
  title: React.ReactNode;
  message: React.ReactNode;
  confirmLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  variant?: 'danger' | 'warning' | 'info';
  loading?: boolean;
  /** Notice-only mode: hide the confirm button, leaving just the close
   * button — for dialogs that inform/warn without an action to confirm. */
  hideConfirm?: boolean;
}

const ConfirmDialog_: React.FC<ConfirmDialogProps> = ({ open, onClose, onConfirm, title, message, confirmLabel, cancelLabel, variant = 'danger', loading, hideConfirm }) => {
  const [confirming, setConfirming] = useState(false);
  const handleConfirm = useCallback(async () => {
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      setConfirming(false);
    }
    onClose();
  }, [onConfirm, onClose]);
  const busy = loading || confirming;

  const iconColorClass = {
    danger: 'text-error-red-500',
    warning: 'text-warning-yellow-500',
    info: 'text-french-violet-600',
  }[variant];

  return (
    <Modal.Container open={open} onClose={onClose} panelClassName="max-w-sm">
      <div className="flex items-start gap-4">
        <div
          className={classNames('flex-shrink-0 p-2 rounded-full bg-opacity-10', iconColorClass, {
            'bg-error-red-200': variant === 'danger',
            'bg-warning-yellow-200': variant === 'warning',
            'bg-french-violet-50': variant === 'info',
          })}
        >
          <ExclamationTriangleIcon className={classNames('w-6 h-6', iconColorClass)} />
        </div>
        <div className="flex-1">
          <Modal.Header className="!mb-2">
            <span className="font-semibold">{title}</span>
          </Modal.Header>
          <p className="text-sm text-gray-700 dark:text-gray-300">{message}</p>
        </div>
      </div>
      <Modal.Footer className="justify-end gap-3">
        <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
          {cancelLabel || <T _str="cancel" swc />}
        </Button>
        {hideConfirm ? null : variant === 'danger' ? (
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            className="px-3 py-2 m-1 text-base font-medium text-white bg-error-red-500 border-2 border-error-red-500 rounded-full hover:bg-error-red-700 hover:border-error-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {confirmLabel || <T _str="delete" swc />}
          </button>
        ) : variant === 'warning' ? (
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            className="px-3 py-2 m-1 text-base font-medium text-white bg-warning-yellow-500 border-2 border-warning-yellow-500 rounded-full hover:bg-warning-yellow-700 hover:border-warning-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {confirmLabel || <T _str="confirm" swc />}
          </button>
        ) : (
          <Button variant="primary" size="sm" onClick={handleConfirm} loading={busy}>
            {confirmLabel || <T _str="confirm" swc />}
          </Button>
        )}
      </Modal.Footer>
    </Modal.Container>
  );
};

export const ConfirmDialog = memo(ConfirmDialog_);
